import * as React from "react"
import {
  useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, getFilteredRowModel,
  flexRender, type ColumnDef, type SortingState, type ColumnFiltersState, type VisibilityState,
} from "@tanstack/react-table"
import {
  ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Search, SlidersHorizontal, Download, FileSpreadsheet, FileText,
} from "lucide-react"

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { shouldIgnoreRowClick } from "@/lib/rowOpen"
import { getDefaultPageSize } from "@/lib/gridDensity"
import { exportToExcel, exportToPdf } from "@/utils/tableExport"

// Lets a column opt into a clean export value / header, or out of export entirely.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends unknown, TValue> {
    exportValue?: (row: TData) => string | number | null | undefined
    exportHeader?: string
    noExport?: boolean
  }
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  searchKey?: string
  searchPlaceholder?: string
  pageSize?: number
  showPagination?: boolean
  showSearch?: boolean
  /** Open this record. The row becomes clickable; the Actions column keeps
   *  working — clicks that land on a button/link/checkbox inside the row are
   *  theirs, not the row's (see lib/rowOpen). */
  onRowClick?: (row: TData) => void
  isLoading?: boolean
  emptyMessage?: string
  className?: string
  // new (all optional, default on):
  enableColumnSelector?: boolean
  enableExport?: boolean
  exportFileName?: string
  exportTitle?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchPlaceholder = "Search…",
  // Unset falls back to the device's Core Settings ▸ Appearance choice — the
  // setting whose whole job is "how many rows does a grid open with". A screen
  // that passes a size still wins.
  pageSize = getDefaultPageSize(),
  showPagination = true,
  showSearch = true,
  onRowClick,
  isLoading = false,
  emptyMessage = "No results.",
  className,
  enableColumnSelector = true,
  enableExport = true,
  exportFileName = "export",
  exportTitle,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})
  const [globalFilter, setGlobalFilter] = React.useState("")

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { sorting, columnFilters, columnVisibility, rowSelection, globalFilter },
    initialState: { pagination: { pageSize } },
  })

  const colLabel = (col: any): string => {
    const meta = col.columnDef.meta as any
    if (meta?.exportHeader) return meta.exportHeader
    const h = col.columnDef.header
    return typeof h === "string" ? h : (col.id as string)
  }

  const buildExport = () => {
    const cols = table.getVisibleLeafColumns().filter((c) => {
      const meta = c.columnDef.meta as any
      return !meta?.noExport && c.id !== "select" && c.id !== "actions"
    })
    const headers = cols.map(colLabel)
    const rows = table.getSortedRowModel().rows.map((r) =>
      cols.map((c) => {
        const meta = c.columnDef.meta as any
        if (meta?.exportValue) return meta.exportValue(r.original as TData)
        try {
          const v = r.getValue(c.id)
          return v && typeof v === "object" ? JSON.stringify(v) : (v as any)
        } catch {
          const v = (r.original as any)?.[c.id]
          return v && typeof v === "object" ? "" : v
        }
      })
    )
    return { headers, rows }
  }

  const doExport = (kind: "excel" | "pdf") => {
    const { headers, rows } = buildExport()
    const hiddenColumnCount = table
      .getAllLeafColumns()
      .filter((c) => c.getCanHide() && c.id !== "select" && !c.getIsVisible()).length
    // What the grid is actually showing, so the file says so on its face.
    const spec = {
      fileName: exportFileName,
      headers,
      rows,
      title: exportTitle,
      meta: {
        ...(globalFilter ? { Filter: `"${globalFilter}"` } : {}),
        ...(hiddenColumnCount > 0 ? { Columns: `${headers.length} of ${headers.length + hiddenColumnCount} shown` } : {}),
      },
    }
    const run = kind === "excel" ? exportToExcel(spec) : exportToPdf(spec)
    Promise.resolve(run).catch((e) => console.error("Export failed", e))
  }

  const hideableColumns = table.getAllLeafColumns().filter((c) => c.getCanHide() && c.id !== "select")
  const showToolbar = showSearch || enableColumnSelector || enableExport

  return (
    <div className={cn("space-y-4", className)}>
      {showToolbar && (
        <div className="flex items-center gap-2">
          {showSearch && (
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-8"
              />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {enableColumnSelector && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <SlidersHorizontal className="h-4 w-4 mr-2" />Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                  <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {hideableColumns.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.id}
                      className="capitalize"
                      checked={col.getIsVisible()}
                      onCheckedChange={(v) => col.toggleVisibility(!!v)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {colLabel(col)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {enableExport && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <Download className="h-4 w-4 mr-2" />Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => doExport("excel")}>
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-success-700" />Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doExport("pdf")}>
                    <FileText className="h-4 w-4 mr-2 text-destructive-700" />PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sorted = header.column.getIsSorted()
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())
                  return (
                    <TableHead key={header.id}>
                      {canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground select-none"
                        >
                          {content}
                          {sorted === "asc" ? <ArrowUp className="h-3.5 w-3.5" />
                            : sorted === "desc" ? <ArrowDown className="h-3.5 w-3.5" />
                            : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
                        </button>
                      ) : content}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={table.getVisibleLeafColumns().length} className="h-24 text-center">
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="ml-2 text-muted-foreground">Loading…</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={onRowClick ? "cursor-pointer" : ""}
                  onClick={
                    onRowClick
                      ? (e) => {
                          if (shouldIgnoreRowClick(e)) return
                          onRowClick(row.original)
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getVisibleLeafColumns().length} className="h-24 text-center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between px-2">
          <div className="flex-1 text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length > 0 ? (
              <span>
                {table.getFilteredSelectedRowModel().rows.length} of{" "}
                {table.getFilteredRowModel().rows.length} row(s) selected.
              </span>
            ) : (
              <span>{table.getFilteredRowModel().rows.length} row(s)</span>
            )}
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(value === "all" ? (data.length || 99999) : Number(value))
                }}
              >
                <SelectTrigger className="h-8 w-[80px]">
                  <SelectValue placeholder={table.getState().pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 20, 50, 100].map((size) => (
                    <SelectItem key={size} value={`${size}`}>{size}</SelectItem>
                  ))}
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Page {table.getState().pagination.pageIndex + 1} of {Math.max(1, table.getPageCount())}
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" className="hidden h-8 w-8 p-0 lg:flex" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                <span className="sr-only">First page</span><ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="h-8 w-8 p-0" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                <span className="sr-only">Previous page</span><ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="h-8 w-8 p-0" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                <span className="sr-only">Next page</span><ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="hidden h-8 w-8 p-0 lg:flex" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
                <span className="sr-only">Last page</span><ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { ArrowUpDown as SortIcon }
